<script setup lang="ts">
  import { FlatMenuList } from '@/router/routes'
  import { CSSProperties } from 'vue'

  const { mobile } = useDisplay()
  const route = useRoute()

  const router = useRouter()

  const banners = computed(() => route.meta.banners ?? ['quick-start', 'server-status', 'classes-and-jobs']) // default value
  // Menu items first - they carry the `__` external-link shape - then any other named route, so
  // a page hidden from the navigation (`hideOnMenu`, e.g. the Classic pages) is still a valid
  // banner target. Kept in the order `meta.banners` lists them.
  const bannerMenus = computed(() =>
    banners.value.flatMap((name) => {
      const found = FlatMenuList.find((x) => x.name === name) ?? router.getRoutes().find((x) => x.name === name)
      return found ? [found] : []
    }),
  )

  // every banner image in the folder, by path - so a missing mobile crop can fall back to the
  // desktop one instead of a broken url
  const images = import.meta.glob('/src/assets/imgs/components/PageBanners/*.webp', {
    eager: true,
    query: '?url',
    import: 'default',
  }) as Record<string, string>
  const getBackgroundImageStyle = (id: string): Partial<CSSProperties> => {
    const image = (suffix: string) => images[`/src/assets/imgs/components/PageBanners/${id}${suffix}.webp`]
    const url = (mobile.value && image('-mobile')) || image('')
    return {
      backgroundImage: `url(${url})`,
    }
  }
</script>
<template>
  <contentWrapper
    v-ga-section-view="route.meta.menuTitle"
    data-event="General Elements"
    data-section-name="Page Suggestion"
    data-section-number="98"
  >
    <PlayNowButton class="my-md-13 my-sm-8 my-4" />
    <div v-if="bannerMenus.length" class="page-banners" :class="{ 'page-banners--pair': bannerMenus.length === 2 }">
      <template v-for="menu in bannerMenus" :key="menu.name">
        <component
          :is="menu.path != '__' ? 'RouterLink' : 'a'"
          v-ga-event="menu.meta?.menuTitle"
          class="page-banners__banner"
          :class="menu.name"
          v-bind="menu.path == '__' ? { href: menu.redirect, target: '_blank' } : { to: { name: menu.name } }"
        >
          <div :style="getBackgroundImageStyle(String(menu.name))">
            <span>{{ menu.meta?.menuTitle }}</span>
          </div>
        </component>
      </template>
    </div>
  </contentWrapper>
</template>
<style lang="scss" scoped>
  .page-banners {
    display: flex;
    align-items: center;
    justify-content: center;
    column-gap: 2%;
  }
  .page-banners__banner {
    width: 32%;
    height: pxToRem(170);
    display: inline-block;
    border-radius: pxToRem(10);
    max-width: 1600px;
    cursor: pointer;
    font-size: pxToRem(45);
    color: #fff;
    font-weight: 300;
    text-transform: uppercase;
    line-height: 1;
    overflow: hidden;
    > div {
      display: flex;
      align-items: center;
      padding: 0 10.25rem 0 1rem;
      height: 100%;
      width: 100%;
      transition: all 0.3s;
      background-size: cover;
      will-change: transform;
    }
    &:hover {
      > div {
        transform: scale(1.05);
      }
    }
  }
  @media #{$tablet} {
    // the banners are a fixed width here, so the gap is whatever three of them leave over
    .page-banners {
      column-gap: calc((100% - 3 * #{pxToRemTablet(306)}) / 2);
    }
    .page-banners__banner {
      width: pxToRemTablet(306);
      height: pxToRemTablet(106);
      font-size: pxToRemTablet(30);
      > div {
        padding: 0 4rem 0 0.25rem;
      }
    }
    // Two banners: the MSCW 1024 artboards draw them 460.8 x 153 on a 27 gap, the 40.5 label
    // 23.4 in from the left with 157.5 kept clear of the art on the right
    .page-banners--pair {
      column-gap: pxToRemTablet(27);
      .page-banners__banner {
        width: pxToRemTablet(460.8);
        height: pxToRemTablet(153);
        font-size: pxToRemTablet(40.5);
        > div {
          padding: 0 pxToRemTablet(157.5) 0 pxToRemTablet(23.4);
        }
      }
    }
  }
  @media #{$mobile} {
    .page-banners {
      flex-direction: column;
    }
    .page-banners__banner {
      width: pxToRemMobile(552);
      height: pxToRemMobile(120);
      font-size: pxToRemMobile(42);
      border-radius: pxToRemMobile(15);
      > div {
        padding: 0 12rem 0 1rem;
      }
      &:not(:last-child) {
        margin-bottom: pxToRemMobile(15);
      }
    }
    // The pair keeps the mobile sizes - the tablet pair rule above would otherwise win on
    // specificity - and the MSCW 600 artboards stack it 20 apart rather than 15
    .page-banners--pair .page-banners__banner {
      width: pxToRemMobile(552);
      height: pxToRemMobile(120);
      font-size: pxToRemMobile(42);
      > div {
        padding: 0 12rem 0 1rem;
      }
      &:not(:last-child) {
        margin-bottom: pxToRemMobile(20);
      }
    }
  }
</style>
